import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Layers, ArrowRight, Plus, Star, Percent, Search, X } from 'lucide-react';
import { CATEGORIES, SUB_CATEGORY_LABELS, CATEGORY_LABELS, PRICING_MODEL_LABELS } from '../../utils/planningConstants';
import { formatPrice } from '../../utils/planningHelpers';

export default function ServiceGrid({ hub }) {
  const {
    services, filteredServices, packages, selectedCategory, setSelectedCategory,
    selectedSubCategory, setSelectedSubCategory, dropoffOnly, setDropoffOnly,
    serviceSearch, setServiceSearch, availability,
    loading, currentSubCategories, eventDetails,
    openPackageDetail, addToCart, setViewingService, setSelectedGalleryImage,
    setDetailTab, setSelectedOptions, getPackageEstimate
  } = hub;

  const openServiceDetail = (service) => {
    setViewingService(service);
    setSelectedGalleryImage(0);
    setDetailTab('overview');
    const defaults = {};
    service.option_groups?.forEach(og => {
      const defaultChoices = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
      if (defaultChoices.length > 0) defaults[og.option_id] = og.multiple_select ? defaultChoices : defaultChoices[0];
    });
    setSelectedOptions(defaults);
  };

  const quickAddOrCustomize = (e, service) => {
    e.stopPropagation();
    setViewingService(service);
    setSelectedGalleryImage(0);
    if (service.option_groups?.length > 0) {
      setDetailTab('customize');
      const defaults = {};
      service.option_groups?.forEach(og => {
        const defaultChoices = og.choices?.filter(c => c.is_default).map(c => c.choice_id) || [];
        if (defaultChoices.length > 0) defaults[og.option_id] = og.multiple_select ? defaultChoices : defaultChoices[0];
      });
      setSelectedOptions(defaults);
    } else {
      addToCart(service);
    }
  };

  return (
    <div className="flex-1 min-w-0">
      {/* Category Tabs */}
      <div className="flex gap-1.5 sm:gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
        {CATEGORIES.map(cat => (
          <Button key={cat.id} variant={selectedCategory === cat.id ? 'default' : 'outline'} size="sm" onClick={() => { setSelectedCategory(cat.id); setSelectedSubCategory(null); }} className="flex-shrink-0 text-xs sm:text-sm px-2.5 sm:px-3">
            <cat.icon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" /><span className="hidden sm:inline">{cat.name}</span><span className="sm:hidden">{cat.id === 'all' ? 'All' : cat.id === 'packages' ? 'Pkgs' : cat.name.split(' ')[0]}</span>
          </Button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="relative mb-4" data-testid="service-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search services... (e.g. taco, DJ, photo booth)"
          value={serviceSearch}
          onChange={(e) => setServiceSearch(e.target.value)}
          className="pl-10 pr-10"
          data-testid="service-search-input"
        />
        {serviceSearch && (
          <button
            onClick={() => setServiceSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="service-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sub-category filter */}
      {currentSubCategories.length > 0 && (
        <div className="flex gap-1.5 sm:gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          <Button variant={selectedSubCategory === null ? 'secondary' : 'ghost'} size="sm" onClick={() => setSelectedSubCategory(null)} className="text-xs flex-shrink-0">
            All {CATEGORIES.find(c => c.id === selectedCategory)?.name}
          </Button>
          {currentSubCategories.map(sub => (
            <Button key={sub} variant={selectedSubCategory === sub ? 'secondary' : 'ghost'} size="sm" onClick={() => setSelectedSubCategory(sub)} className="text-xs flex-shrink-0">
              {SUB_CATEGORY_LABELS[sub] || sub}
            </Button>
          ))}
        </div>
      )}

      {/* Drop-off Filter */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 p-2.5 sm:p-3 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Checkbox id="dropoff-filter" checked={dropoffOnly} onCheckedChange={setDropoffOnly} data-testid="dropoff-filter" />
          <label htmlFor="dropoff-filter" className="text-xs sm:text-sm font-medium cursor-pointer flex items-center gap-1.5 sm:gap-2">
            <span className="text-base sm:text-lg">🚚</span>
            <span className="hidden sm:inline">Drop-off Available Only</span>
            <span className="sm:hidden">Drop-off Only</span>
          </label>
        </div>
        <span className="text-[10px] sm:text-xs text-muted-foreground">
          ({services.filter(s => s.has_dropoff_option).length} with drop-off)
        </span>
      </div>

      {/* Services Grid — shown first */}
      {selectedCategory !== 'packages' && (
        loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map(i => (<div key={i} className="h-64 rounded-lg bg-muted animate-pulse" />))}
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-12"><p className="text-muted-foreground">No services available for your criteria</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredServices.map(service => (
              <Card key={service.service_id} className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${availability[service.service_id]?.status === 'fully_booked' ? 'opacity-60' : ''}`} onClick={() => openServiceDetail(service)} data-testid={`service-card-${service.service_id}`}>
                {service.image_url && (
                  <div className="h-40 sm:h-48 overflow-hidden relative">
                    <img src={service.image_url} alt={service.name} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex gap-1.5 sm:gap-2">
                      {service.is_featured && <Badge className="bg-amber-500 text-[10px] sm:text-xs">Featured</Badge>}
                      {service.has_dropoff_option && <Badge className="bg-emerald-500 text-[10px] sm:text-xs">Drop-off</Badge>}
                    </div>
                    {availability[service.service_id] && (
                      <div className="absolute bottom-2 left-2" data-testid={`avail-badge-${service.service_id}`}>
                        {availability[service.service_id].status === 'fully_booked' ? (
                          <Badge className="bg-red-600 text-white text-[10px]">Fully Booked</Badge>
                        ) : availability[service.service_id].status === 'limited' ? (
                          <Badge className="bg-amber-500 text-white text-[10px]">Limited Availability</Badge>
                        ) : availability[service.service_id].status === 'check_availability' ? (
                          <Badge className="bg-blue-600 text-white text-[10px]">Check Availability</Badge>
                        ) : availability[service.service_id].status === 'available' ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]">Instant Book</Badge>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-base sm:text-lg">{service.name}</CardTitle>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] sm:text-xs">{CATEGORY_LABELS[service.category] || service.category}</Badge>
                    {service.sub_category && <Badge variant="outline" className="text-[10px] sm:text-xs">{SUB_CATEGORY_LABELS[service.sub_category] || service.sub_category}</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="pb-2">
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{service.short_description}</p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div>
                      <span className="text-xl sm:text-2xl font-bold text-indigo-500">{formatPrice(service)}</span>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">{PRICING_MODEL_LABELS[service.pricing_model]}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {service.avg_rating && (
                        <span className="flex items-center gap-1 text-sm text-yellow-500" data-testid={`hub-rating-${service.item_id}`}>
                          <Star className="h-3.5 w-3.5 fill-yellow-500" />{service.avg_rating}
                          <span className="text-muted-foreground text-xs">({service.review_count})</span>
                        </span>
                      )}
                      {service.option_groups?.length > 0 && <Badge variant="outline" className="text-xs">Customizable</Badge>}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-2">
                  {availability[service.service_id]?.status === 'fully_booked' ? (
                    <Button className="w-full" variant="outline" disabled>Fully Booked</Button>
                  ) : (
                    <Button className="w-full" onClick={(e) => quickAddOrCustomize(e, service)}>
                      {service.option_groups?.length > 0 ? 'Customize & Add' : <><Plus className="mr-2 h-4 w-4" /> Add to Event</>}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Packages divider — below Individual Services */}
      {(selectedCategory === 'all' || selectedCategory === 'packages') && packages.length > 0 && (
        <>
          {selectedCategory === 'all' && (
            <div className="flex items-center gap-3 my-8">
              <Layers className="h-5 w-5 text-indigo-500" />
              <h2 className="text-lg font-bold">Curated Packages</h2>
              <div className="h-px flex-1 bg-border" />
              <Button variant="ghost" size="sm" onClick={() => setSelectedCategory('packages')} className="text-indigo-500">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {packages.map(pkg => {
              const categories = [...new Set((pkg.services || []).map(s => s.category))];
              const serviceCount = (pkg.services || []).length;
              const estimate = getPackageEstimate(pkg);
              return (
                <Card key={pkg.package_id} className="overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openPackageDetail(pkg)} data-testid={`package-card-${pkg.package_id}`}>
                  {pkg.image_url && (
                    <div className="h-40 sm:h-48 overflow-hidden relative">
                      <img src={pkg.image_url} alt={pkg.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <Badge className="bg-indigo-600 text-white border-0 text-xs"><Layers className="h-3 w-3 mr-1" /> Package</Badge>
                        {pkg.is_featured && <Badge className="bg-amber-500 text-white border-0 text-xs">Featured</Badge>}
                      </div>
                      {pkg.discount_type !== 'none' && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-green-500 text-white border-0 text-xs"><Percent className="h-3 w-3 mr-1" /> {pkg.discount_type === 'percentage' ? `${pkg.discount_value}% off` : `$${pkg.discount_value} off`}</Badge>
                        </div>
                      )}
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="flex flex-wrap gap-1.5">
                          {(pkg.services || []).slice(0, 3).map(svc => (
                            <span key={svc.service_id} className="text-xs text-white/90 bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full flex items-center gap-1">
                              <span className="text-sm">{svc.category === 'food' ? '🍽️' : svc.category === 'drinks' ? '🍸' : svc.category === 'entertainment' ? '🎵' : svc.category === 'staffing' ? '👥' : '📦'}</span>
                              {svc.name.length > 16 ? svc.name.substring(0, 16) + '...' : svc.name}
                            </span>
                          ))}
                          {(pkg.services || []).length > 3 && <span className="text-xs text-white/70 px-2 py-0.5">+{(pkg.services || []).length - 3} more</span>}
                        </div>
                      </div>
                    </div>
                  )}
                  <CardHeader className="pb-2 pt-4">
                    <CardTitle className="text-base sm:text-lg group-hover:text-indigo-500 transition-colors leading-tight">{pkg.name}</CardTitle>
                    <div className="flex gap-1.5 flex-wrap">
                      {categories.map(cat => (<Badge key={cat} variant="outline" className="text-[10px] sm:text-xs capitalize">{cat}</Badge>))}
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{serviceCount} services</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{pkg.short_description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        {estimate.perPerson > 0 ? (
                          <>
                            <span className="text-xs text-muted-foreground">From </span>
                            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">${estimate.perPerson.toFixed(0)}</span>
                            <span className="text-xs sm:text-sm text-muted-foreground">/pp</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-muted-foreground">From </span>
                            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">${estimate.fixed.toFixed(0)}</span>
                          </>
                        )}
                        {pkg.discount_type !== 'none' && (
                          <span className="ml-2 text-xs text-green-500 font-medium">Save {pkg.discount_type === 'percentage' ? `${pkg.discount_value}%` : `$${pkg.discount_value}`}</span>
                        )}
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" onClick={(e) => { e.stopPropagation(); openPackageDetail(pkg); }} data-testid={`view-package-${pkg.package_id}`}>
                      <Layers className="mr-2 h-4 w-4" /> View Package
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Packages empty state */}
      {selectedCategory === 'packages' && packages.length === 0 && (
        <div className="text-center py-12">
          <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No curated packages available yet</p>
        </div>
      )}
    </div>
  );
}
